import React from 'react';
import EventNotification from '../../interfaces/eventNotification/EventNotification';

export type ToastType = 'success' | 'info' | 'error' | 'warning';

export const getToastType = (notification: EventNotification): ToastType => {
    const { type } = notification;

    if (type?.includes('error') || type?.includes('failed') || type?.includes('rejected')) {
        return 'error';
    }
    if (type?.includes('success') || type?.includes('completed') || type?.includes('approved')) {
        return 'success';
    }
    if (type?.includes('warn') || type?.includes('warning') || type?.includes('attention')) {
        return 'warning';
    }
    return 'info';
};

export const renderNotificationTitle = (notification: EventNotification) => {
    const { title, notificationResources } = notification;

    // Default fallback if no title exists
    if (!title) return <span>Notification</span>;

    let processedTitle = title;

    // Early exit if no resources are available
    if (!notificationResources || notificationResources.length === 0) {
        return <span>{processedTitle}</span>;
    }

    // 1. Group resources by their actual type for efficient lookup.
    const resourceMap = new Map<string, EventNotification['notificationResources']>();
    notificationResources.forEach(resource => {
        if (!resourceMap.has(resource.resourceType)) {
            resourceMap.set(resource.resourceType, []);
        }
        resourceMap.get(resource.resourceType)?.push(resource);
    });

    // 2. Define special mappings from a placeholder name to a resourceType.
    // (Keep this consistent with renderNotificationMessage)
    const placeholderToTypeMap: { [key: string]: string } = {
        author: 'user',
        activity: 'task_action',
        // file: 'FILE'
    };

    // 3. Find all placeholders and replace them with the Resource Name
    const interpolatedTitle = processedTitle.replace(/\{(\w+)\}/g, (match, placeholder) => {
        // Use the mapping if it exists; otherwise, the placeholder name is the resource type.
        const resourceType = placeholderToTypeMap[placeholder] || placeholder;

        const availableResources = resourceMap.get(resourceType);

        if (!availableResources || availableResources.length === 0) {
            return match;
        }

        // Consume the next available resource of this type.
        const resource = availableResources.shift();
        if (!resource) return match;

        // For the title, we usually just want to bold the name, not add icons/links
        return `<strong>${resource.resourceName}</strong>`;
    });

    return <span dangerouslySetInnerHTML={{ __html: interpolatedTitle }} />;
};

export const renderNotificationMessage = (notification: EventNotification) => {
    const { message, notificationResources, type } = notification;
    let processedMessage = message;

    // Early exit if no resources are available for a message that isn't a simple string.
    if (!notificationResources || notificationResources.length === 0) {
        return <span>{processedMessage}</span>;
    }

    // Handle ticket-specific notifications that contain a raw GUID in the message.
    if (type && (type.startsWith('ticket.'))) {
        const ticketResource = notificationResources.find(r => r.resourceType === 'ticket');
        if (ticketResource) {
            const guidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
            processedMessage = processedMessage.replace(guidRegex, (match) => {
                if (ticketResource.resourceId.includes(match)) {
                    return `<a href="${ticketResource.resourceId}" class="notification-resource-link"><i class="fas fa-ticket-alt" style="margin-right: 0.25rem;"></i>${ticketResource.resourceName}</a>`;
                }
                return match;
            });
        }
    }

    // 1. Group resources by their actual type for efficient lookup.
    const resourceMap = new Map<string, EventNotification['notificationResources']>();
    notificationResources.forEach(resource => {
        if (!resourceMap.has(resource.resourceType)) {
            resourceMap.set(resource.resourceType, []);
        }
        resourceMap.get(resource.resourceType)?.push(resource);
    });

    // 2. Define special mappings from a placeholder name to a resourceType.
    const placeholderToTypeMap: { [key: string]: string } = {
        author: 'user',
        activity: 'task_action',
        // file: 'FILE'
    };

    // 3. Find all placeholders and replace them using the logic below.
    const interpolatedMessage = processedMessage.replace(/\{(\w+)\}/g, (match, placeholder) => {
        // Use the mapping if it exists; otherwise, the placeholder name is the resource type.
        const resourceType = placeholderToTypeMap[placeholder] || placeholder;

        const availableResources = resourceMap.get(resourceType);

        if (!availableResources || availableResources.length === 0) {
            return match; // Return the original placeholder like "{task}" if no resource is found.
        }

        // Consume the next available resource of this type.
        const resource = availableResources.shift();
        if (!resource) return match;

        let linkPath = '';
        let iconHtml = '';

        switch (resource.resourceType) {
            case 'user':
                // The placeholder is {author}, but the resourceType is 'user'.
                linkPath = `/profile${resource.resourceId}`; // Profile links have a special path.
                return `<a href="${linkPath}" class="notification-resource-link"><strong>${resource.resourceName}</strong></a>`;

            case 'task':
                linkPath = `${resource.resourceId}`; // Using your original link location.
                iconHtml = `<i class="fas fa-thumb-tack" style="margin-right: 0.25rem;"></i>`;
                break;

            case 'ticket':
                linkPath = `${resource.resourceId}`;
                iconHtml = `<i class="fas fa-ticket-alt" style="margin-right: 0.25rem;"></i>`;
                break;

            case 'file':
                linkPath = `${resource.resourceId}`; // Using your original link location.
                iconHtml = `<i class="fas fa-file" style="margin-right: 0.25rem;"></i>`;
                break;

            case 'task_action':
                // The placeholder is {activity}, but the resourceType is 'task_action'.
                linkPath = `${resource.resourceId}`; // Using your original link location.
                iconHtml = `<i class="fas fa-bolt" style="margin-right: 0.25rem;"></i>`;
                break;

            case 'meeting':
                linkPath = `${resource.resourceId}`; // Using your original link location.
                iconHtml = `<i class="fas fa-video" style="margin-right: 0.25rem;"></i>`;
                break;

            case 'LAUNCHSURVEY':
                linkPath = `${resource.resourceId}`; // Using your original link location.
                iconHtml = `<i class="fas fa-video" style="margin-right: 0.25rem;"></i>`;
                break;

            case 'FILE':
                linkPath = `${resource.resourceId}`; // Using your original link location.
                iconHtml = `<i class="fas fa-video" style="margin-right: 0.25rem;"></i>`;
                break;

            default:
                return `<strong>${resource.resourceName}</strong>`;
        }

        return `<a href="${linkPath}" class="notification-resource-link">${iconHtml}${resource.resourceName}</a>`;
    });

    return <span dangerouslySetInnerHTML={{ __html: interpolatedMessage }} />;
};